
import { Component, Vue, Emit } from 'vue-property-decorator';

import {
  Heading,
  Row,
  BaseText,
  ModalButton,
  ButtonPrimary,
  ActionableHelperView
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { Steps } from '../PortfolioSuggestedModal/PortfolioSuggestedModal.vue';
import { isImpersonate } from '@/modules/common/services/partner';
import { SuggestionModule } from '@/modules/home/store/suggestions';
import {
  investmentAgreement,
  getInstrumentProductValidation,
  EInvestmentAgreementStatus,
  IGetInstrumentProductValidationError,
  IGetInstrumentProductValidationErrorTypes,
  IGetInstrumentProductValidationResponse,
  IInvestmentAgreementPayload,
  IInvestmentSuggestion
} from '@/modules/wealth/services/investment';
import { RiskValidation } from '@/modules/common/components/RiskValidation';
import { namespace } from 'vuex-class';
import { ICustomer, IInvestmentProfile } from '@/types/models';
import * as trackerInvestmentSuggesttedDetails from './track-investment-events';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { SuggestionType } from '@/modules/investments/types/suggestion';
import format from 'date-fns/format';
import * as trackerRedeemSuggestionDetails from './track-redeem-events';
import { getCustomerInvestmentProfile } from '@/modules/common/services/http/account';

const customerModule = namespace('CustomerModule');

@Component({
  components: {
    Heading,
    Row,
    BaseText,
    ModalButton,
    ButtonPrimary,
    RiskValidation,
    ActionableHelperView
  }
})
export default class InvestmentSuggestedDetails extends Vue {
  @customerModule.Getter('getCustomer')
  public customer?: ICustomer;

  @customerModule.Getter('getCustomerId')
  private customerId!: string;

  readonly NebraskaColors = NebraskaColors;
  public isImpersonate!: boolean;

  public showRiskValidation = false;
  public error = false;
  public risksAndTerms: IGetInstrumentProductValidationResponse | null = null;
  public isLoadingSubmit = false;
  public isLoadingValidation = false;
  public investmentProfile?: IInvestmentProfile = {};

  async created() {
    this.isImpersonate = isImpersonate();
    this.trackLoadProduct();
    await this.getCustomerProfile();
  }

  private async getCustomerProfile() {
    if (this.customer) {
      const { investmentProfile } = await getCustomerInvestmentProfile(
        this.customer._id
      );
      this.investmentProfile = investmentProfile;
    }
  }

  private trackLoadProduct() {
    if (this.isRedeemSuggestion) {
      this.portfolioSuggestion.products.forEach(product => {
        trackerRedeemSuggestionDetails.trackLoadProductDetails(product);
      });
    }
  }

  private async instrumentProductsValidation() {
    this.isLoadingValidation = true;
    this.isLoadingSubmit = true;
    try {
      this.risksAndTerms = await getInstrumentProductValidation(
        this.productsIds,
        this.customerId,
        this.portfolioSuggestion.toPortfolioApiId
      );
      return true;
    } catch (err) {
      const error = err as IGetInstrumentProductValidationError;
      if (error?.error.key) {
        const { message } = error.error;
        SuggestionModule.setValidationErrorMessage(message.formatted);
        SuggestionModule.setValidationErrorTitle(
          message.value ===
            IGetInstrumentProductValidationErrorTypes.OutsideOperationTime
            ? 'Sugestão indisponível!'
            : 'Sugestão Inválida!'
        );
        SuggestionModule.setValidationErrorType(message.value);
        this.modalNavigate(Steps.Error);
        this.isLoadingSubmit = false;
        return false;
      }
    } finally {
      this.isLoadingValidation = false;
    }
  }

  @Emit('modal-navigate')
  private modalNavigate(step: Steps) {
    return step;
  }

  public handlerRefuse() {
    this.trackRefuseSuggestion();
    this.modalNavigate(Steps.Refuse);
  }

  private trackRefuseSuggestion() {
    if (!this.isRedeemSuggestion) {
      trackerInvestmentSuggesttedDetails.trackingRefusalSuggestedInvestment();
      trackerInvestmentSuggesttedDetails.refuseInvestmentSuggestedAmplitude();
    } else {
      trackerRedeemSuggestionDetails.trackRefuseRedeemSuggestion(
        this.portfolioSuggestion.amount.value
      );
    }
  }

  public async finishSuggestedInvestment() {
    if (this.isImpersonate) return;

    this.isLoadingSubmit = true;
    this.error = false;

    const payloadInvestmentAgrrement: IInvestmentAgreementPayload = {
      transactionId: this.portfolioSuggestion.transactionId,
      customerId: this.customerId,
      status: EInvestmentAgreementStatus.Accepted,
      updatedBy: this.customerId
    };

    try {
      await investmentAgreement(payloadInvestmentAgrrement);
      SuggestionModule.removeSuggestion(
        payloadInvestmentAgrrement.transactionId
      );
      this.modalNavigate(Steps.List);
      this.notifyUser();
    } catch {
      this.error = true;
    } finally {
      this.isLoadingSubmit = false;
    }
  }

  private notifyUser() {
    this.trackSuggestionSuccessful();
    this.$emit('notify-user', {
      group: 'wrn-notification-portfolio-suggestion',
      text: !this.isRedeemSuggestion
        ? 'Investimento em carteira aceito!'
        : 'Resgate de carteira aceito!',
      type: NotificationTypes.success
    });
  }

  private trackSuggestionSuccessful() {
    if (!this.isRedeemSuggestion) {
      trackerInvestmentSuggesttedDetails.trackingCreatePortfolio();
    } else {
      trackerRedeemSuggestionDetails.trackRedeemSuccessful();
    }
  }

  public async acceptSuggestion() {
    if (!this.isRedeemSuggestion) {
      const isInvesmentValid = await this.instrumentProductsValidation();

      if (!isInvesmentValid) {
        this.isLoadingSubmit = false;
        return;
      }
    }
    this.trackAcceptSuggestion();

    if (this.risksAndTerms) {
      const { investmentProductValidation } = this.risksAndTerms;
      if (
        investmentProductValidation.requestTerms ||
        investmentProductValidation.requestRisks
      ) {
        this.showRiskValidation = true;
        return;
      }
    }
    this.finishSuggestedInvestment();
  }

  private trackAcceptSuggestion() {
    if (!this.isRedeemSuggestion) {
      trackerInvestmentSuggesttedDetails.trackingSuggestedInvestment();
      trackerInvestmentSuggesttedDetails.acceptInvestmentSuggestedAmplitude();
    } else {
      trackerRedeemSuggestionDetails.trackAcceptRedeemSuggestion(
        this.portfolioSuggestion.amount.value
      );
    }
  }

  public onRiskValidationFinish() {
    this.finishSuggestedInvestment();
    this.showRiskValidation = false;
  }

  public onRiskValidationAbort() {
    this.showRiskValidation = false;
  }

  public get portfolioSuggestion() {
    return SuggestionModule.suggestionDetails.detail as IInvestmentSuggestion;
  }

  public get productsIds() {
    return this.portfolioSuggestion.products.map(item => item.productApiId);
  }

  private get isRedeemSuggestion() {
    return SuggestionModule.suggestionType === SuggestionType.Redeem;
  }

  public get title() {
    const typeOfInvestment = this.isRedeemSuggestion
      ? 'resgate'
      : 'investimento';
    return `Características do ${typeOfInvestment}`;
  }

  public get investmentSummaryTitle() {
    const suggestionTypeLabel = this.isRedeemSuggestion
      ? 'resgatado'
      : 'investido';
    return `Valor total a ser ${suggestionTypeLabel}`;
  }

  formatDate(date: string) {
    return format(new Date(date), `dd/MM/yyyy 'às' HH:mm`);
  }
}
